import { gql } from '@apollo/client';
import {
  Client,
  Contract,
  ContractContent,
  ContractFilterInput,
  Invoice,
  InvoiceChargeAttempt,
  InvoiceLineItem,
  InvoiceStatus,
  PaginatedContracts,
  Product,
} from './types';

/**
 * One page of contracts for the Contracts page table.
 *
 * Filtering, searching and paging all happen server-side: the page holds a
 * single page of rows at a time and re-queries whenever the filter or the
 * page changes. `limit` is clamped to [1, 200] by the resolver, and the order
 * is fixed (createdAt DESC) — there is no sort argument.
 */
export const CONTRACTS_QUERY = gql`
  query AllContracts($filter: ContractFilterInput, $limit: Int, $offset: Int) {
    contracts(filter: $filter, limit: $limit, offset: $offset) {
      items {
        id
        startDate
        endDate
        status
        nickname
        declineReason
        pdfTemplateVersion
        pdfS3Url
        salesforceOpportunity
        createdBy
        createdAt
        updatedAt
        client {
          id
          businessName
        }
        signatureProof {
          envelopeId
          signerEmail
          signedAt
          signerIp
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPrevPage
      }
    }
  }
`;

export interface ContractsQueryData {
  // The contracts list doesn't render line items, so it doesn't fetch `contents` — avoids an N+1.
  contracts: PaginatedContracts;
}

export interface ContractsQueryVariables {
  filter?: ContractFilterInput;
  limit?: number;
  offset?: number;
}

/**
 * Every contract belonging to a single Bench client, newest first, with its
 * product lines attached.
 *
 * Same `contracts` field and `ContractFilterInput` as CONTRACTS_QUERY above —
 * callers pin it to one client with `filter: { clientIds: [id] }`. The
 * difference is that this one *does* select `contents`: the N+1 the list query
 * avoids isn't a concern when the filter is a single client, since a client has
 * at most a handful of contracts. That lets a consumer find the ACTIVE contract
 * and render its lines from one round trip.
 *
 * No `offset`: 200 is the resolver's ceiling and no client comes close to that
 * many contracts, so a client's whole history fits on one page.
 */
export const CONTRACTS_FOR_CLIENT_QUERY = gql`
  query ContractsForClient($filter: ContractFilterInput, $limit: Int) {
    contracts(filter: $filter, limit: $limit) {
      items {
        id
        startDate
        endDate
        status
        nickname
        declineReason
        createdBy
        createdAt
        client {
          id
          businessName
        }
        contents {
          id
          startDate
          endDate
          quantity
          discountType
          discountAmount
          discountDuration
          product {
            sku
            displayName
            campaign
            name
            productLine
            billingType
            cadence
            pricePerPayment
            canBeSold
          }
        }
      }
      totalCount
    }
  }
`;

/** A contract with its product lines resolved — what CONTRACTS_FOR_CLIENT_QUERY returns. */
export type ContractWithContents = Contract & {
  contents: (ContractContent & { product: Product })[];
};

export interface ContractsForClientQueryData {
  // Nullable: consumers run this with errorPolicy 'all', which resolves with a
  // `data` object whose failed fields are null rather than withholding `data`.
  contracts:
    | (Omit<PaginatedContracts, 'items' | 'pageInfo'> & {
        items: ContractWithContents[];
      })
    | null;
}

export interface ContractsForClientQueryVariables {
  filter?: ContractFilterInput;
  limit?: number;
}

/**
 * Full contract details, including products linked via ContractContent.
 */
export const CONTRACT_DETAILS_QUERY = gql`
  query ContractDetails($id: ID!) {
    contract(id: $id) {
      id
      startDate
      endDate
      status
      nickname
      declineReason
      pdfTemplateVersion
      pdfS3Url
      salesforceOpportunity
      createdBy
      createdAt
      updatedAt
      client {
        id
        businessName
      }
      defaultSigner {
        email
        name
      }
      signatureProof {
        envelopeId
        signerEmail
        signedAt
        signerIp
      }
      contents {
        id
        startDate
        endDate
        quantity
        discountType
        discountAmount
        discountDuration
        product {
          sku
          displayName
          campaign
          name
          productLine
          billingType
          cadence
          pricePerPayment
          canBeSold
        }
      }
    }
  }
`;

export interface ContractDetailsQueryData {
  contract:
    | (Contract & {
        contents: (ContractContent & { product: Product })[];
      })
    | null;
}

export interface ContractDetailsQueryVariables {
  id: string;
}

export const CONTRACT_PDF_PREVIEW_QUERY = gql`
  query ContractPdfPreview($contractId: ID!) {
    contractPdfPreview(contractId: $contractId)
  }
`;

export interface ContractPdfPreviewQueryData {
  contractPdfPreview: string;
}

export interface ContractPdfPreviewQueryVariables {
  contractId: string;
}

/**
 * Product catalog for the Products page.
 */
export const PRODUCTS_QUERY = gql`
  query Products {
    products {
      sku
      displayName
      campaign
      name
      productLine
      billingType
      cadence
      pricePerPayment
      canBeSold
    }
  }
`;

export interface ProductsQueryData {
  products: Product[];
}

/**
 * Invoices for a single Bench client. Drives the Invoices page when the user
 * filters by client. Returns the full per-line breakdown including the
 * payment processor transaction reference, which the by-status query omits.
 */
export const INVOICES_FOR_CLIENT_QUERY = gql`
  query InvoicesForClient($clientId: ID!) {
    invoicesForClient(clientId: $clientId) {
      id
      origin
      status
      expectedChargeDate
      actualChargeDate
      paymentTransactionId
      chargeStatus
      client {
        id
      }
      contract {
        id
      }
      lineItems {
        id
        price
        quantity
        startDate
        endDate
        discountType
        discountAmount
        product {
          sku
        }
      }
    }
  }
`;

export interface InvoicesForClientQueryData {
  invoicesForClient: (Invoice & {
    client: Pick<Client, 'id'>;
    contract: Pick<Contract, 'id'>;
    lineItems: (InvoiceLineItem & {
      product: Pick<Product, 'sku'>;
    })[];
  })[];
}

export interface InvoicesForClientQueryVariables {
  clientId: string;
}

/**
 * Invoices across all clients filtered by lifecycle status. Drives the
 * Invoices page when the user filters by status.
 */
export const INVOICES_BY_STATUS_QUERY = gql`
  query InvoicesByStatus($status: InvoiceStatus!) {
    invoicesByStatus(status: $status) {
      id
      status
      origin
      expectedChargeDate
      actualChargeDate
      chargeStatus
      client {
        id
      }
      contract {
        id
      }
      lineItems {
        product {
          sku
        }
        startDate
        endDate
        discountAmount
      }
    }
  }
`;

export interface InvoicesByStatusQueryData {
  invoicesByStatus: (Omit<Invoice, 'paymentTransactionId' | 'lineItems'> & {
    client: Pick<Client, 'id'>;
    contract: Pick<Contract, 'id'>;
    lineItems: (Pick<InvoiceLineItem, 'startDate' | 'endDate' | 'discountAmount'> & {
      product: Pick<Product, 'sku'>;
    })[];
  })[];
}

export interface InvoicesByStatusQueryVariables {
  status: InvoiceStatus;
}

/**
 * Full detail view for a single invoice — drives the Invoice Details page.
 * Returns the full per-line breakdown together with the parent contract
 * summary so the page doesn't have to follow up with a second fetch.
 */
export const GET_INVOICE_QUERY = gql`
  query GetInvoice($id: ID!) {
    invoice(id: $id) {
      id
      origin
      status
      expectedChargeDate
      actualChargeDate
      paymentTransactionId
      helcimInvoiceNumber
      chargeStatus
      subtotal
      taxAmount
      total
      client {
        id
      }
      contract {
        id
        salesforceOpportunity
        startDate
        endDate
        status
      }
      lineItems {
        id
        invoiceId
        displayName
        price
        quantity
        startDate
        endDate
        discountType
        discountAmount
        product {
          sku
          displayName
          productLine
          billingType
          cadence
          pricePerPayment
        }
      }
    }
  }
`;

export interface GetInvoiceQueryData {
  invoice:
    | (Invoice & {
        client: Pick<Client, 'id'>;
        contract: Pick<
          Contract,
          'id' | 'salesforceOpportunity' | 'startDate' | 'endDate' | 'status'
        >;
        lineItems: (InvoiceLineItem & {
          product: Pick<
            Product,
            'sku' | 'displayName' | 'productLine' | 'billingType' | 'cadence' | 'pricePerPayment'
          > | null;
        })[];
      })
    | null;
}

export interface GetInvoiceQueryVariables {
  id: string;
}

/**
 * Charge-attempt audit trail for a single invoice — one immutable row per charge
 * attempt, including the ones a pre-Helcim guard stopped. The backend orders them
 * oldest → newest and returns an empty array (never null) for an unknown invoice.
 *
 * Kept separate from GetInvoice so a failure to load the audit trail can't take
 * the invoice details page down with it.
 */
export const CHARGE_ATTEMPTS_FOR_INVOICE_QUERY = gql`
  query ChargeAttemptsForInvoice($invoiceId: ID!) {
    chargeAttemptsForInvoice(invoiceId: $invoiceId) {
      id
      outcome
      chargedAmount
      createdAt
      helcimTransactionId
      helcimInvoiceNumber
      helcimStatus
      approvalCode
      cardType
      cardLast4
      errorHttpStatus
      errorMessage
    }
  }
`;

export interface ChargeAttemptsForInvoiceQueryData {
  chargeAttemptsForInvoice: InvoiceChargeAttempt[];
}

export interface ChargeAttemptsForInvoiceQueryVariables {
  invoiceId: string;
}
